import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { Box, Collapse, Grid, Stack } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { toJS } from "mobx";
import React, { useState } from "react";
import { LabeledConversationsChip } from "../../Components/LabeledConversationsChip";
import { LLMConfigurationBuilderProvider } from "../../Components/LLMConfigurationBuilder";
import { LogicChainBuilder } from "../../Components/LogicChainBuilder/LogicChainBuilder";
import { StandardBuilder } from "../../Components/StandardBuilder/StandardBuilder";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxSlider from "components/UI/AcxSlider";
import AcxMenu, {
    AcxMenuItemProps,
    StyledMenuLabel,
} from "components/UI/Menu/AcxMenu";
import { DataLabelingModuleAndQuestionSelectors } from "../../Components/DataLabelingModuleAndQuestionSelector";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";
import {
    DateRangeFilter,
    HierarchiesFilter,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents";
import {
    ApplicationFilterItemsStore,
    FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { FilterComponent } from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterComponent";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { ChainSourceSection } from "../../Components/ChainSource/ChainSourceSection";
import { ModelChainPanel } from "../../Components/ChainSource/ModelChainPanel";

export const LLMClassifierBuilder = observer(() => {
    return (
        <LLMConfigurationBuilderProvider>
            {/* Wraps on the width actually available rather than on a viewport
                breakpoint, since the nav rail and page padding mean a wide
                viewport does not guarantee room for both columns. Once the row
                cannot fit the two flex bases, the map drops below full width. */}
            <Stack
                direction="row"
                flexWrap="wrap"
                columnGap={2}
                rowGap={2}
                alignItems="flex-start"
                width="100%"
            >
                <Stack
                    direction="column"
                    rowGap={2}
                    sx={{ flex: "999 1 34rem", minWidth: 0 }}
                >
                    <ChainSourceSection />
                    <BuilderSections />
                </Stack>
                {/* Sticky so the map stays in view while editing prompts far
                    down the page, but height bound: a tall chain would
                    otherwise have its bottom pinned off screen with no way to
                    reach it. Drop `position` to let it scroll away instead. */}
                <Box
                    sx={{
                        flex: "1 1 20rem",
                        maxHeight: "calc(100vh - 88px)",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflowY: "auto",
                        position: "sticky",
                        top: "64px",
                    }}
                >
                    <ModelChainPanel />
                </Box>
            </Stack>
        </LLMConfigurationBuilderProvider>
    );
});

const BuilderSections = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);

    return (
        <>
            <TitledSection
                variant="bordered"
                title={
                    <Grid
                        container
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Grid item>
                            Model Rules
                            {window["enable_debug"] === true &&
                                process.env.NODE_ENV === "development" && (
                                    <AcxButton
                                        onClick={() =>
                                            console.log(
                                                toJS(
                                                    store.classifier
                                                        .implementation,
                                                ),
                                            )
                                        }
                                    >
                                        Log State
                                    </AcxButton>
                                )}
                        </Grid>
                    </Grid>
                }
            >
                {store.classifier.implementation.isStandard() && (
                    <StandardBuilder />
                )}
                {store.classifier.implementation.isLogicChain() && (
                    <LogicChainBuilder />
                )}
            </TitledSection>
            <TitledSection
                title="Test Model"
                subtitle={
                    store.isOnConversationsTesting()
                        ? "Select filters to narrow down conversations you want to test against"
                        : "Select a module and question to test this model against"
                }
                variant="bordered"
                sx={{ width: "100%" }}
            >
                {store.isAgreementTesting() && (
                    <>
                        <DataLabelingModuleAndQuestionSelectors />
                        {store.classifier.implementation.isLogicChain() && (
                            <Grid
                                container
                                item
                                direction="row"
                                paddingTop={1}
                                columnGap={1}
                            >
                                {store.classifier.outputs.map((output) => (
                                    <Grid item>
                                        <LabeledConversationsChip
                                            result={output.result}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
                {store.isOnConversationsTesting() && (
                    <TestWithoutLabelsOptions />
                )}
                <TestModelButtons />
            </TitledSection>
        </>
    );
});

type TestModelButtonProps = React.ComponentPropsWithoutRef<typeof AcxButton> & {
    sampleSize?: number;
};

const TestModelButton = observer(function (props: TestModelButtonProps) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    return (
        <AcxButton
            {...props}
            onClick={(event) => {
                if (props.onClick) props.onClick(event);
                else {
                    if (store.isOnConversationsTesting()) {
                        store.testLLMClassifierOnConversations(
                            props.sampleSize,
                        );
                    } else {
                        store.testLLMClassifierAgreement({
                            sampleSize: props.sampleSize,
                        });
                    }
                    stepper.setCurrentWizardStep("testing");
                }
            }}
            disabled={!store.isLLMClassifierTestable() || props.disabled}
        >
            {props.children}
        </AcxButton>
    );
});

const TestModelButtons = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    const hasTested =
        !!store.testAgreementResponse || !!store.testOnConversationsResponse;
    const testingWord = hasTested ? "Retest" : "Test";

    const [isNTestDialogOpen, setIsNTestDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [sampleSize, setSampleSize] = useState(50);
    const isSampleSizeOne = sampleSize === 1;
    const menuItems = store.classifier.outputs.map<AcxMenuItemProps>(
        (output) => ({
            id: "menu-item-test-one-result",
            props: {
                onClick: () => {
                    store.testLLMClassifierAgreement({
                        isTestOne: true,
                        testOneLabel: output.result,
                    });
                    stepper.setCurrentWizardStep("testing");
                },
            },
            label: <StyledMenuLabel>Test {output.result}</StyledMenuLabel>,
        }),
    );

    let sampleSizeToUse = sampleSize;

    return (
        <Grid
            container
            item
            columnGap={1}
            marginTop={2}
            justifyContent="flex-end"
        >
            <AcxMenu
                anchorElement={menuAnchor}
                menuItems={menuItems}
                onMenuClose={() => setMenuAnchor(null)}
            />
            {store.isAgreementTesting() && (
                <AcxButton
                    onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                    }}
                    disabled={!store.isLLMClassifierTestable()}
                >
                    {testingWord} one
                </AcxButton>
            )}
            {store.isOnConversationsTesting() && (
                <TestModelButton sampleSize={1}>
                    {testingWord} one
                </TestModelButton>
            )}
            <AcxButton
                disabled={!store.isLLMClassifierTestable()}
                onClick={() => setIsNTestDialogOpen(true)}
            >
                {testingWord} n (up to {store.maxModelTestSampleSizeForUser})
            </AcxButton>
            <TestModelButton
                sampleSize={LLMClassifierBuilderStore.MaxModelTestSampleSize}
            >
                {testingWord} all (up to {store.maxModelTestSampleSizeForUser})
            </TestModelButton>
            {hasTested && (
                <AcxButton
                    color="white"
                    onClick={() => stepper.setCurrentWizardStep("testing")}
                >
                    View last test results
                </AcxButton>
            )}
            <ConfirmationDialog
                onClose={() => setIsNTestDialogOpen(false)}
                onCancel={() => setIsNTestDialogOpen(false)}
                isOpen={isNTestDialogOpen}
                confirmButtonText={`Test ${sampleSizeToUse}`}
                onConfirm={() => {
                    if (store.isOnConversationsTesting()) {
                        store.testLLMClassifierOnConversations(sampleSizeToUse);
                    } else {
                        store.testLLMClassifierAgreement({
                            sampleSize: sampleSizeToUse,
                        });
                    }
                    stepper.setCurrentWizardStep("testing");
                }}
                content={
                    <Stack direction="column" rowGap={2}>
                        <AcxSlider
                            value={sampleSize}
                            onChange={(value) => {
                                setSampleSize(value);
                            }}
                            underSlideLabel={`${
                                isSampleSizeOne ? "" : "Up to "
                            }${sampleSize} conversation${
                                isSampleSizeOne ? "" : "s"
                            }${store.isAgreementTesting() ? " per tag" : ""}`}
                            minValue={1}
                            maxValue={
                                LLMClassifierBuilderStore.MaxModelTestSampleSize
                            }
                        />
                        or
                        <AcxMainTextField
                            id="sample-size-input"
                            labelText="Input number of conversations"
                            value={sampleSize}
                            placeholderText={`Enter sample size, max ${LLMClassifierBuilderStore.MaxModelTestSampleSize}`}
                            type="number"
                            insertBlankIfNumber
                            onChange={(event) => {
                                let value = event.target.valueAsNumber;
                                if (!value || isNaN(value) || value < 0) return;
                                if (
                                    value >
                                    LLMClassifierBuilderStore.MaxModelTestSampleSize
                                ) {
                                    return setSampleSize(
                                        LLMClassifierBuilderStore.MaxModelTestSampleSize,
                                    );
                                }

                                setSampleSize(value);
                            }}
                        />
                    </Stack>
                }
            />
        </Grid>
    );
});

const additionalFilters: { key: FilterKey; label?: string }[] = [
    { key: "callDuration", label: "Call Duration" },
    { key: "mediaTypes", label: "Media Types" },
    { key: "evaluationTypes", label: "Evaluation Types" },
    { key: "agentIds", label: "Agents" },
    { key: "favoriteConversations", label: "Favorite Conversations" },
    { key: "eddyEffectStatus", label: "Eddy Effect" },
    { key: "hipaaCompliance", label: "HIPAA Compliance" },
    { key: "beginningSentiment", label: "Beginning Sentiment" },
    { key: "endingSentiment", label: "Ending Sentiment" },
    { key: "safetyEvent", label: "Safety Event" },
    { key: "contactTypes", label: "Contact Types" },
    { key: "patientJourneyStages", label: "Patient Journey Stages" },
    { key: "modelResults" },
    { key: "questionResults", label: "Question Results" },
    // The two Theme filters share this label and are told apart by a "(Deprecated)" marker added at
    // render time, for the viewers who can see that status. See
    // ApplicationFilterItemsStore.withDeprecationMarker.
    { key: "topics", label: "Primary Theme" },
    { key: "themes", label: "Primary Theme" },
    { key: "primaryTopics", label: "Primary Topic" },
    { key: "rbcFilterItems" },
    { key: "wordsAndPhrasesSearchString" },
    { key: "dataLabels", label: "Data Labeling" },
];

const TestWithoutLabelsOptions = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    return (
        <Grid container item direction="column" flexWrap="nowrap" rowGap={1}>
            <Grid container item direction="row" columnGap={1}>
                <Grid item>
                    Date Range
                    <DateRangeFilter
                        filterKey="dateRange"
                        store={store.testOnConversationsFilters}
                    />
                </Grid>
                <Grid item>
                    Hierarchies
                    <HierarchiesFilter
                        filterKey="hierarchyIds"
                        store={store.testOnConversationsFilters}
                        popperPlacement="right-start"
                    />
                </Grid>
            </Grid>
            <Collapse in={showMoreFilters} timeout="auto" unmountOnExit>
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                    sx={() => ({ maxWidth: "50%" })}
                >
                    {additionalFilters.map((filter) => (
                        <Grid item key={filter.key}>
                            {filter.label &&
                                applicationFilterItemsStore.withDeprecationMarker(
                                    filter.key,
                                    filter.label,
                                )}
                            <FilterComponent
                                filterKey={filter.key}
                                store={store.testOnConversationsFilters}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Collapse>
            <AcxButton
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                variant="outlined"
            >
                {showMoreFilters ? "Show less" : "Show more filters"}
            </AcxButton>
        </Grid>
    );
});
